import { useEffect, useRef, useState } from "react";
import { HiOutlineDownload, HiOutlineUpload, HiOutlineX } from "react-icons/hi";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useCalibrationCertificateImport } from "effects";
import { useDispatch, useSelector } from "store";
import { fetchAssets, fetchStorages } from "store/reducers";
import { selectAssetsList } from "store/reducers/assets/assets.selectors";
import { selectStorages } from "store/reducers/storages/storages.selectors";
import { TAsset } from "types";
import { SecondaryButton } from "components/shared/buttons";
import { Heading, TextBody } from "components/shared/typography";

dayjs.extend(utc);

// The shared Button sizes leading icons for full-size buttons; these keep the compact
// table and header buttons looking right while still using the shared component.
const DownloadIcon = (props: { className?: string }) => <HiOutlineDownload {...props} className="h-4 w-4 mr-1.5" />;

const UploadIcon = (props: { className?: string }) => <HiOutlineUpload {...props} className="h-3.5 w-3.5 mr-1.5" />;

const ExportCsvButton = ({ onClick }: { onClick: () => void }) => (
  <SecondaryButton
    width="w-auto"
    flex="flex-none"
    textSize="text-sm"
    fontWeight="font-medium"
    paddingY="py-1.5"
    paddingX="px-3"
    leadingIcon={DownloadIcon}
    onClick={onClick}
  >
    Export CSV
  </SecondaryButton>
);

const formatDate = (dateStr: string): string => dayjs(dateStr).format("DD MMM YYYY");

const getCalibrationStatus = (nextDue: string): { label: string; variant: "red" | "yellow" | "green" } => {
  const daysUntil = dayjs.utc(nextDue).diff(dayjs.utc(), "day");
  if (daysUntil < 0) return { label: "Overdue", variant: "red" };
  if (daysUntil <= 30) return { label: `Due in ${daysUntil} day${daysUntil === 1 ? "" : "s"}`, variant: "yellow" };
  return { label: `Due in ${daysUntil} days`, variant: "green" };
};

const variantClasses = {
  red: "bg-red-100 text-red-700",
  yellow: "bg-yellow-100 text-yellow-700",
  green: "bg-green-100 text-green-700",
};

const CalibrationStatusBadge = ({ nextDue }: { nextDue: string }) => {
  const { label, variant } = getCalibrationStatus(nextDue);
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${variantClasses[variant]}`}
    >
      {label}
    </span>
  );
};

type CalibrationRow = {
  asset: TAsset;
  toolName: string;
  serialNumber: string;
  toolbox: string;
  nextDue: string;
  status: string;
};

const exportCSV = (rows: CalibrationRow[]) => {
  const headers = ["Tool", "Serial Number", "Toolbox", "Next Due", "Status"];
  const csvRows = [
    headers.join(","),
    ...rows.map((r) =>
      [r.toolName, r.serialNumber, r.toolbox, r.nextDue, r.status].map((v) => `"${v.replace(/"/g, '""')}"`).join(","),
    ),
  ];
  const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `calibration-${dayjs().format("YYYY-MM-DD")}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

// ── Circle stat ──────────────────────────────────────────────────────────────

const circleColors = {
  red: "#ef4444",
  yellow: "#eab308",
  green: "#22c55e",
  gray: "#9ca3af",
};

type CircleStatProps = {
  label: string;
  value: number;
  total: number;
  color?: keyof typeof circleColors;
  active?: boolean;
  onClick: () => void;
};

const CircleStat = ({ label, value, total, color = "gray", active = false, onClick }: CircleStatProps) => {
  const radius = 56;
  const strokeWidth = 7;
  const normalizedRadius = radius - strokeWidth;
  const circumference = 2 * Math.PI * normalizedRadius;
  const pct = total > 0 ? Math.min((value / total) * 100, 100) : 0;
  const offset = circumference - (pct / 100) * circumference;
  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center gap-3 rounded-xl p-3 transition-colors focus:outline-none ${
        active ? "bg-gray-100 ring-2 ring-gray-300" : "hover:bg-gray-50"
      }`}
    >
      <div className="relative flex items-center justify-center" style={{ width: radius * 2, height: radius * 2 }}>
        <svg height={radius * 2} width={radius * 2} style={{ transform: "rotate(-90deg)" }}>
          <circle
            r={normalizedRadius}
            cx={radius}
            cy={radius}
            fill="transparent"
            stroke="#e5e7eb"
            strokeWidth={strokeWidth}
          />
          <circle
            r={normalizedRadius}
            cx={radius}
            cy={radius}
            fill="transparent"
            stroke={circleColors[color]}
            strokeWidth={strokeWidth}
            strokeDasharray={`${circumference} ${circumference}`}
            strokeDashoffset={offset}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute text-center">
          <span className="text-2xl font-semibold text-gray-900">{value}</span>
        </div>
      </div>
      <p className="text-sm font-medium text-gray-500 text-center leading-tight max-w-[7rem]">{label}</p>
    </button>
  );
};

// ── Detail panel ─────────────────────────────────────────────────────────────

type FilterKey = "all" | "overdue" | "within30" | "within60" | "beyond60";

const filterTitles: Record<FilterKey, string> = {
  all: "All calibrated tools",
  overdue: "Overdue tools",
  within30: "Due within 30 days",
  within60: "Due within 31–60 days",
  beyond60: "Due in 60+ days",
};

type DetailPanelProps = {
  title: string;
  rows: CalibrationRow[];
  onClose: () => void;
};

const DetailPanel = ({ title, rows, onClose }: DetailPanelProps) => {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 p-4">
      <div
        ref={panelRef}
        className="relative w-full max-w-2xl bg-white rounded-xl shadow-2xl flex flex-col"
        style={{ maxHeight: "80vh" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
          <div>
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Calibration</p>
            <h2 className="text-lg font-semibold text-gray-900 mt-0.5">{title}</h2>
          </div>
          <div className="flex items-center gap-2">
            {rows.length > 0 && <ExportCsvButton onClick={() => exportCSV(rows)} />}
            <button
              onClick={onClose}
              className="ml-1 rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
            >
              <HiOutlineX className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1">
          {rows.length === 0 ? (
            <p className="px-6 py-10 text-sm text-gray-400 text-center">No tools in this category.</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Tool
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Toolbox
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Next due
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map(({ asset, toolName, toolbox, nextDue }) => (
                  <tr key={asset.id} className="hover:bg-gray-50">
                    <td className="px-6 py-3">
                      <Link
                        to={`/apps/tracking/assets/${asset.id}/calibration`}
                        onClick={onClose}
                        className="text-sm font-medium text-gray-900 hover:underline"
                      >
                        {toolName}
                      </Link>
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-500 whitespace-nowrap">{toolbox}</td>
                    <td className="px-6 py-3 text-sm text-gray-700 whitespace-nowrap">{nextDue}</td>
                    <td className="px-6 py-3">
                      <CalibrationStatusBadge nextDue={asset.calibrationConfig!.nextDue!} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-100 shrink-0 text-xs text-gray-400">
          {rows.length} {rows.length === 1 ? "tool" : "tools"}
        </div>
      </div>
    </div>
  );
};

// ── Main tab ─────────────────────────────────────────────────────────────────

export const CalibrationDashboardTab = () => {
  const dispatch = useDispatch();
  const allAssets = useSelector(selectAssetsList);
  const storages = useSelector(selectStorages);
  const { enabled: aiImportEnabled, disabledReason, openImport } = useCalibrationCertificateImport();
  const openImportModal = (asset: TAsset) =>
    openImport(asset, () => dispatch(fetchAssets({ pageStart: 0, pageLimit: 999, calibrated: true })));
  const [activeFilter, setActiveFilter] = useState<FilterKey | null>(null);

  useEffect(() => {
    dispatch(fetchAssets({ pageStart: 0, pageLimit: 999, calibrated: true }));
    dispatch(fetchStorages({ pageStart: 0, pageLimit: 999 }));
  }, []);

  const rows: CalibrationRow[] = allAssets
    .filter((a) => a.calibrationConfig?.nextDue)
    .sort((a, b) => (dayjs(a.calibrationConfig!.nextDue).isBefore(dayjs(b.calibrationConfig!.nextDue)) ? -1 : 1))
    .map((asset) => {
      const storage = asset.storageConfig?.storageId ? storages[asset.storageConfig.storageId] : undefined;
      return {
        asset,
        toolName: asset.description || asset.catalogProduct?.name || `Asset #${asset.id}`,
        serialNumber: asset.serialNumber || "—",
        toolbox: storage?.name || "—",
        nextDue: formatDate(asset.calibrationConfig!.nextDue!),
        status: getCalibrationStatus(asset.calibrationConfig!.nextDue!).label,
      };
    });

  const overdueRows = rows.filter((r) => dayjs.utc(r.asset.calibrationConfig!.nextDue!).diff(dayjs.utc(), "day") < 0);
  const within30Rows = rows.filter((r) => {
    const d = dayjs.utc(r.asset.calibrationConfig!.nextDue!).diff(dayjs.utc(), "day");
    return d >= 0 && d <= 30;
  });
  const within60Rows = rows.filter((r) => {
    const d = dayjs.utc(r.asset.calibrationConfig!.nextDue!).diff(dayjs.utc(), "day");
    return d > 30 && d <= 60;
  });
  const beyond60Rows = rows.filter((r) => {
    const d = dayjs.utc(r.asset.calibrationConfig!.nextDue!).diff(dayjs.utc(), "day");
    return d > 60;
  });

  const filterRows: Record<FilterKey, CalibrationRow[]> = {
    all: rows,
    overdue: overdueRows,
    within30: within30Rows,
    within60: within60Rows,
    beyond60: beyond60Rows,
  };

  const toggleFilter = (key: FilterKey) => setActiveFilter((prev) => (prev === key ? null : key));

  return (
    <div className="flex flex-col gap-6 pt-4">
      <div className="flex flex-wrap justify-center gap-6 py-4">
        <CircleStat
          label="Total calibrated tools"
          value={rows.length}
          total={rows.length}
          color="gray"
          active={activeFilter === "all"}
          onClick={() => toggleFilter("all")}
        />
        <CircleStat
          label="Overdue"
          value={overdueRows.length}
          total={rows.length}
          color={overdueRows.length > 0 ? "red" : "gray"}
          active={activeFilter === "overdue"}
          onClick={() => toggleFilter("overdue")}
        />
        <CircleStat
          label="Due within 30 days"
          value={within30Rows.length}
          total={rows.length}
          color={within30Rows.length > 0 ? "yellow" : "gray"}
          active={activeFilter === "within30"}
          onClick={() => toggleFilter("within30")}
        />
        <CircleStat
          label="Due within 60 days"
          value={within60Rows.length}
          total={rows.length}
          color="green"
          active={activeFilter === "within60"}
          onClick={() => toggleFilter("within60")}
        />
        <CircleStat
          label="Due 60+ days"
          value={beyond60Rows.length}
          total={rows.length}
          color="gray"
          active={activeFilter === "beyond60"}
          onClick={() => toggleFilter("beyond60")}
        />
      </div>

      <div className="flex items-center justify-between">
        <Heading level="h3">Tool calibration expiry</Heading>
        {rows.length > 0 && <ExportCsvButton onClick={() => exportCSV(rows)} />}
      </div>

      {rows.length === 0 ? (
        <TextBody>No tools with calibration tracking configured.</TextBody>
      ) : (
        <div className="rounded-lg ring-1 ring-slate-900/10 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 bg-white">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tool</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Serial number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Toolbox
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Next due
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map(({ asset, toolName, serialNumber, toolbox, nextDue }) => (
                <tr key={asset.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <Link
                      to={`/apps/tracking/assets/${asset.id}/calibration`}
                      className="font-medium text-gray-900 hover:underline"
                    >
                      {toolName}
                    </Link>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">{serialNumber}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{toolbox}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">{nextDue}</td>
                  <td className="px-6 py-4">
                    <CalibrationStatusBadge nextDue={asset.calibrationConfig!.nextDue!} />
                  </td>
                  <td className="px-4 py-4 text-right">
                    <SecondaryButton
                      width="w-auto"
                      flex="flex-none"
                      textSize="text-xs"
                      fontWeight="font-medium"
                      paddingY="py-1"
                      paddingX="px-2"
                      leadingIcon={UploadIcon}
                      onClick={() => openImportModal(asset)}
                      disabled={!aiImportEnabled}
                      title={disabledReason || "Import certificate with AI"}
                      className="disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Import
                    </SecondaryButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeFilter && (
        <DetailPanel
          title={filterTitles[activeFilter]}
          rows={filterRows[activeFilter]}
          onClose={() => setActiveFilter(null)}
        />
      )}
    </div>
  );
};
